import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AutomationStore, type GroupRow } from "./AutomationStore";
import { SectionCard } from "../common/SectionCard";
import { PieChart } from "../../dashboard/components/base/PieChart";
import { GroupByChart } from "../../dashboard/components/base/GroupByChart";

const toChart = (rows: GroupRow[]) => rows.map((r) => ({ groupKey: r.key, count: r.count }));

const PRESETS: { label: string; hours: number }[] = [
  { label: "Hoy", hours: 24 },
  { label: "2 días", hours: 48 },
  { label: "Última semana", hours: 24 * 7 },
];

/** Analítica: distribución ACTUAL (tortas/barras de los widgets) + HISTÓRICO
 *  por período (hoy/48h/7d/desde fecha, agregación client-side). */
export const AnalyticsTab = observer(({ store }: { store: AutomationStore }) => {
  useEffect(() => {
    if (store.historyEvents.length === 0) void store.loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  return (
    <Stack spacing={2}>
      {/* Ahora */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <PieChart title="Workflow DNDA — por estado" data={toChart(store.dndaStateDist)} maxItems={10} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <PieChart title="Workflow SADAIC — por estado" data={toChart(store.sadaicStateDist)} maxItems={10} />
        </Box>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <GroupByChart title="Bots por tipo" data={toChart(store.botRunByType)} maxItems={10} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <GroupByChart title="Jobs del worker — por estado" data={toChart(store.workerEventByStatus)} maxItems={10} />
        </Box>
      </Stack>

      {/* Histórico */}
      <SectionCard
        title="Histórico"
        subtitle="Jobs del worker por día (completados / fallidos / agotados), según el período."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <ButtonGroup size="small">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  variant={
                    Math.abs(Date.now() - store.historySince.getTime() - p.hours * 3600 * 1000) < 60000
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() => store.setHistorySince(new Date(Date.now() - p.hours * 3600 * 1000))}
                >
                  {p.label}
                </Button>
              ))}
            </ButtonGroup>
            <TextField
              type="date"
              size="small"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                const d = e.target.value ? new Date(e.target.value) : null;
                if (d && !isNaN(d.getTime())) store.setHistorySince(d);
              }}
            />
            {store.historyLoading && <CircularProgress size={18} />}
          </Stack>
        }
      >
        <HistoryBars store={store} />
      </SectionCard>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <GroupByChart title="Histórico — jobs por pipeline" data={toChart(store.historyByPipeline)} maxItems={10} />
        </Box>
        <Box sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
});

/** Barras apiladas por día (completed verde / failed naranja / dead_letter rojo). */
const HistoryBars = observer(({ store }: { store: AutomationStore }) => {
  const theme = useTheme();
  const rows = store.historyByDay;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        — sin actividad en el período —
      </Typography>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.completed + r.failed + r.dead_letter + r.otros));
  const seg = (count: number, color: string, label: string, day: string) =>
    count > 0 ? (
      <Tooltip key={label} title={`${day}: ${count} ${label}`}>
        <Box sx={{ height: 18, width: `${(count / max) * 420}px`, minWidth: 3, bgcolor: color }} />
      </Tooltip>
    ) : null;
  return (
    <Stack spacing={0.75}>
      {rows.map((r) => (
        <Stack key={r.day} direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ width: 90 }} color="text.secondary">
            {r.day}
          </Typography>
          <Stack direction="row" sx={{ borderRadius: 0.5, overflow: "hidden" }}>
            {seg(r.completed, theme.palette.success.main, "completados", r.day)}
            {seg(r.otros, theme.palette.primary.light, "en curso/otros", r.day)}
            {seg(r.failed, theme.palette.warning.main, "fallidos", r.day)}
            {seg(r.dead_letter, theme.palette.error.main, "agotados", r.day)}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {r.completed + r.failed + r.dead_letter + r.otros}
          </Typography>
        </Stack>
      ))}
      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
        {[
          ["Completados", theme.palette.success.main],
          ["En curso/otros", theme.palette.primary.light],
          ["Fallidos", theme.palette.warning.main],
          ["Agotados (dead letter)", theme.palette.error.main],
        ].map(([label, color]) => (
          <Stack key={label as string} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 12, height: 12, bgcolor: color as string, borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
});
